import { Add, Close, Remove, Save } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { soulManager } from "../data/managers/SoulManager";
import { CreditApplication } from "../data/models/applications/CreditApplication";
import { SoulDataModel } from "../data/models/Soul/SoulDataModel";
import { fmtMoney } from "../utils/NumberTool";

const GREEN = "#00C853";
const DARK = "#0D1420";

interface Props {
  open: boolean;
  credit: CreditApplication | null;
  onClose: () => void;
  onApprove: (data: {
    creditId: string;
    guarantorId: string;
    interestRate: number;
    approvalDate: string;
    updates: Partial<CreditApplication>;
  }) => Promise<void>;
}

const ApproveCreditDialog: React.FC<Props> = ({ open, credit, onClose, onApprove }) => {
  const [form, setForm] = useState<Partial<CreditApplication>>({});
  const [guarantors, setGuarantors] = useState<SoulDataModel[]>([]);
  const [loadingGuarantors, setLoadingGuarantors] = useState<boolean>(true);
  const [selectedGuarantor, setSelectedGuarantor] = useState<SoulDataModel | null>(null);
  const [interestRate, setInterestRate] = useState<number>(6);
  const [approvalDate, setApprovalDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [approving, setApproving] = useState(false);
  const [paymentType, setPaymentType] = useState<"CAPITAL_INTEREST" | "INTEREST_ONLY">("CAPITAL_INTEREST");

  useEffect(() => {
    if (credit) setForm(credit);
  }, [credit]);

  useEffect(() => {
    if (!open) return;
    const fetchGuarantors = async () => {
      try {
        setLoadingGuarantors(true);
        const data = await soulManager.getAllSoulsGuarantors();
        setGuarantors(data!);
      } finally {
        setLoadingGuarantors(false);
      }
    };
    fetchGuarantors();
  }, [open]);

  const handleApprove = async () => {
    if (!credit || !selectedGuarantor) return;
    try {
      setApproving(true);
      await onApprove({
        creditId: credit.id!,
        guarantorId: selectedGuarantor.userId!,
        interestRate,
        approvalDate,
        updates: {
          ...form,
          guarantorName: selectedGuarantor.fullName,
          guarantorDocId: selectedGuarantor?.document ?? "",
          paymentType,
        },
      });
      onClose();
    } finally {
      setApproving(false);
    }
  };

  if (!credit) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>
      {/* Header */}
      <Box sx={{ bgcolor: DARK, px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 800, fontSize: 18, color: "white" }}>Aprobar crédito</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "white", bgcolor: "#ffffff18" }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, bgcolor: "#f7f9f7" }}>
        <Box sx={{ p: 2 }}>

          {/* Monto */}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Monto</Typography>
          <TextField
            fullWidth
            placeholder="$ 0"
            value={form.amount ? fmtMoney(form.amount) : ""}
            onChange={(e) => {
              const num = Number(e.target.value.replace(/\D/g, ""));
              setForm((prev) => ({ ...prev, amount: num }));
            }}
            sx={{ mb: 2, bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 20, fontWeight: 800 } }}
          />

          {/* Plazo */}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Plazo</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, bgcolor: "white", borderRadius: 2, border: "1px solid #e0e0e0", p: 1 }}>
            <IconButton size="small" onClick={() => setForm((p) => ({ ...p, termMonths: Math.max(1, (p.termMonths ?? 12) - 1) }))} sx={{ bgcolor: "#f0f0f0" }}>
              <Remove fontSize="small" />
            </IconButton>
            <Typography sx={{ flex: 1, textAlign: "center", fontWeight: 800, fontSize: 20, color: DARK }}>{form.termMonths ?? 12} meses</Typography>
            <IconButton size="small" onClick={() => setForm((p) => ({ ...p, termMonths: Math.min(84, (p.termMonths ?? 12) + 1) }))} sx={{ bgcolor: "#f0f0f0" }}>
              <Add fontSize="small" />
            </IconButton>
          </Box>

          {/* Propósito */}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Propósito</Typography>
          <TextField
            fullWidth
            value={form.purpose || ""}
            onChange={(e) => setForm((prev) => ({ ...prev, purpose: e.target.value }))}
            sx={{ mb: 2, bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />

          {/* Tasa de interés */}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Tasa de interés mensual (%)</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, bgcolor: "white", borderRadius: 2, border: "1px solid #e0e0e0", p: 1 }}>
            <IconButton size="small" onClick={() => setInterestRate((prev) => Math.max(0, Number((prev - 0.5).toFixed(1))))} sx={{ bgcolor: "#f0f0f0" }}>
              <Remove fontSize="small" />
            </IconButton>
            <Typography sx={{ flex: 1, textAlign: "center", fontWeight: 800, fontSize: 20, color: GREEN }}>{interestRate.toFixed(1)}%</Typography>
            <IconButton size="small" onClick={() => setInterestRate((prev) => Number((prev + 0.5).toFixed(1)))} sx={{ bgcolor: "#f0f0f0" }}>
              <Add fontSize="small" />
            </IconButton>
          </Box>

          {/* Fecha de aprobación */}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Fecha de aprobación</Typography>
          <TextField
            fullWidth
            type="date"
            value={approvalDate}
            onChange={(e) => setApprovalDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />

          {/* Avalista solicitado */}
          {form.guarantorName && (
            <Box sx={{ bgcolor: `${GREEN}11`, border: `1px solid ${GREEN}33`, borderRadius: 2, p: 1.5, mb: 2 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", textTransform: "uppercase", letterSpacing: 1 }}>Avalista solicitado</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: DARK }}>{form.guarantorName} — {form.guarantorPhone}</Typography>
            </Box>
          )}

          {/* Tipo de pago */}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Tipo de pago</Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            {[
              { value: "CAPITAL_INTEREST", label: "Capital + Interés" },
              { value: "INTEREST_ONLY", label: "Solo interés" },
            ].map((opt) => (
              <Box
                key={opt.value}
                onClick={() => setPaymentType(opt.value as any)}
                sx={{ flex: 1, p: 1.5, borderRadius: 2, textAlign: "center", cursor: "pointer", border: `2px solid ${paymentType === opt.value ? "#00C853" : "#e0e0e0"}`, bgcolor: paymentType === opt.value ? "#00C85315" : "white", transition: "all 0.2s" }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: paymentType === opt.value ? "#00C853" : "#667788" }}>{opt.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* Selección avalista */}
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Seleccionar avalista</Typography>
          {loadingGuarantors ? (
            <Box sx={{ py: 2, textAlign: "center" }}>
              <CircularProgress size={24} sx={{ color: GREEN }} />
            </Box>
          ) : (
            <Autocomplete
              options={guarantors}
              getOptionLabel={(option) => `${option.fullName} — ${option.documentType} ${option.document}`}
              renderInput={(params) => (
                <TextField {...params} placeholder="Buscar avalista" fullWidth sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              )}
              value={selectedGuarantor}
              onChange={(_, value) => setSelectedGuarantor(value)}
              isOptionEqualToValue={(opt, val) => opt.userId === val.userId}
            />
          )}
        </Box>
      </DialogContent>

      {/* Botones */}
      <Box sx={{ px: 2.5, py: 2, display: "flex", gap: 1.5, bgcolor: "white", borderTop: "1px solid #f0f0f0" }}>
        <Button fullWidth onClick={onClose} sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, border: "1px solid #e0e0e0", color: "#667788" }}>
          Cancelar
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleApprove}
          disabled={!selectedGuarantor || approving}
          sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, bgcolor: GREEN, color: "white", boxShadow: "none", "&:hover": { bgcolor: "#00b048" } }}
        >
          {approving ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Aprobar crédito"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ApproveCreditDialog;
